import type { LocalRow, SeedTables } from '../types/localDb.types'
import { roleId, seedAudit } from './platform.seed'
import { SEED_GROUP, seedId } from './seedId'

/**
 * Konfigurasi Workflow Studio WF-RTN-01 (Return Order RM) — disalin dari
 * `docs/tl-doc/12B-SERVICE-DATA/JAL_12B-SERVICE-DATA-WF-RTN-01.md` §1–§8 (label [C]).
 * Tabel email, tabbing, SLA, dan cross-workflow tidak di-seed karena tidak dijalankan local mode.
 *
 * Penyimpangan yang dicatat di laporan C05:
 * - [GAP-TL-RTN-01] `RTN_REJECTED` terminal di TL, sedangkan BA RTN-02 (dan prototype v7.44) menyebut RO
 *   ditolak "kembali bisa diedit Admin" → ditambah `ACT_RTN_REVISE` (REJECTED → DRAFT, Admin) dan RTN_REJECTED
 *   tidak terminal. BA menang untuk aturan bisnis pengguna.
 * - [GAP-TL-RTN-02] "Batalkan Retur" saat Siap Kirim (BA RTN-02 §7, tugas Penarikan RTN-04) belum ada di TL
 *   (hanya `ACT_RTN_CANCEL_DRAFT`) → ditambah `ACT_RTN_CANCEL_STAGING` (READY_STAGING → CANCELLED, Admin, alasan wajib).
 * - Admin: kondisi `created_by = $CURRENT_USER.id` (TL §3) dipertahankan; Operator tanpa kondisi.
 */

const WORKFLOW_NO = 4
export const WF_RTN_01_ID = seedId(SEED_GROUP.workflow, WORKFLOW_NO)

const scoped = (n: number) => WORKFLOW_NO * 1000 + n

const STATUSES = [
  ['RTN_DRAFT', 'Draft', 1, true, false, '#64748B', 'Draft Retur dibuat oleh Admin RM'],
  [
    'RTN_PENDING_SPV',
    'Pending Approval - Supervisor',
    2,
    false,
    false,
    '#F59E0B',
    'Menunggu persetujuan Supervisor RM'
  ],
  ['RTN_PENDING_KADIV', 'Pending Approval - Kadiv', 3, false, false, '#F59E0B', 'Menunggu persetujuan Kadiv WH RM'],
  [
    'RTN_PENDING_PURCH',
    'Pending Approval - Purchasing',
    4,
    false,
    false,
    '#3B82F6',
    'Menunggu persetujuan Purchasing & tanggal rencana'
  ],
  [
    'RTN_APPROVED',
    'Approved - Menunggu Pengambilan',
    5,
    false,
    false,
    '#10B981',
    'Disetujui penuh, Admin menunjuk petugas picking'
  ],
  ['RTN_IN_PROGRESS', 'Pengambilan Berjalan', 6, false, false, '#06B6D4', 'Operator scan pickup unit NG ke staging'],
  ['RTN_READY_STAGING', 'Siap Kirim - di Staging', 7, false, false, '#8B5CF6', 'Semua unit di ZS-RM, siap dikirim'],
  ['RTN_CLOSED', 'Closed', 8, false, true, '#059669', 'Pengiriman selesai dicatat, SJR terbit'],
  ['RTN_REJECTED', 'Rejected', 9, false, false, '#EF4444', 'Ditolak pada level approval, dapat diedit ulang Admin'],
  ['RTN_CANCELLED', 'Cancelled', 10, false, true, '#94A3B8', 'Retur dibatalkan oleh Admin RM']
] as const

export type RtnStatusCode = (typeof STATUSES)[number][0]

export const rtnStatusId = (code: RtnStatusCode) =>
  seedId(SEED_GROUP.wmlStatus, scoped(STATUSES.findIndex(([c]) => c === code) + 1))

const ACTORS = [
  ['ACT_RTN_ADMIN_RM', 'Admin WH RM', 'admin_wh_rm', { created_by: { _eq: '$CURRENT_USER.id' } }],
  ['ACT_RTN_OP_RM', 'Operator WH RM', 'operator_wh_rm', null],
  ['ACT_RTN_SPV_RM', 'WH RM Supervisor', 'wh_rm_supervisor', { created_by: { _neq: '$CURRENT_USER.id' } }],
  ['ACT_RTN_KADIV_RM', 'Kadiv WH RM', 'kadiv_wh_rm', null],
  ['ACT_RTN_PURCHASING', 'Purchasing Officer', 'purchasing', null],
  // [R] BA/prototype RTN-01: Supervisor dan Kadiv juga boleh membuat RO (keputusan user). Pemilik draft mengelola
  // draft-nya sendiri; kondisi `_eq` menjaga pemisahan tugas (SPV tak bisa menyetujui RO buatannya sendiri).
  ['ACT_RTN_SPV_OWNER', 'WH RM Supervisor (RO Owner)', 'wh_rm_supervisor', { created_by: { _eq: '$CURRENT_USER.id' } }],
  ['ACT_RTN_KADIV_OWNER', 'Kadiv WH RM (RO Owner)', 'kadiv_wh_rm', { created_by: { _eq: '$CURRENT_USER.id' } }]
] as const

export type RtnActorCode = (typeof ACTORS)[number][0]

export const rtnActorId = (code: RtnActorCode) =>
  seedId(SEED_GROUP.wmlActor, scoped(ACTORS.findIndex(([c]) => c === code) + 1))

const COMPONENTS = [
  ['CMP_RTN_SUBMIT', 'Tombol Ajukan Approval', 'ACTION', { color: '#3B82F6', variant: 'primary', icon: 'tabler:send' }],
  ['CMP_RTN_APP_SPV', 'Tombol Approve Supervisor', 'ACTION', { color: '#3B82F6', icon: 'tabler:check' }],
  ['CMP_RTN_APP_KADIV', 'Tombol Approve Kadiv', 'ACTION', { color: '#3B82F6', icon: 'tabler:check' }],
  ['CMP_RTN_APP_PURCH', 'Tombol Approve Purchasing', 'ACTION', { color: '#3B82F6', icon: 'tabler:check' }],
  ['CMP_RTN_REJECT', 'Tombol Reject Retur', 'ACTION', { color: '#EF4444', icon: 'tabler:x' }],
  ['CMP_RTN_ASSIGN_OP', 'Tombol Tugaskan Operator', 'ACTION', {}],
  ['CMP_RTN_SUBMIT_STAGING', 'Tombol Konfirmasi Staging', 'ACTION', {}],
  ['CMP_RTN_SHIP', 'Tombol Catat Pengiriman (SJR)', 'ACTION', {}],
  ['CMP_RTN_CANCEL', 'Tombol Batalkan Retur', 'ACTION', { color: '#EF4444' }],
  ['CMP_RTN_REVISE', 'Tombol Edit & Ajukan Ulang', 'ACTION', { color: '#3B82F6' }],
  ['CMP_RTN_PANEL_HEADER', 'Panel Header Retur', 'PANEL', {}],
  ['CMP_RTN_PANEL_ITEMS', 'Panel Item & Tiket NG', 'PANEL', {}],
  ['CMP_RTN_PANEL_SHIPPING', 'Panel Ekspedisi & SJR', 'PANEL', {}]
] as const

type ComponentCode = (typeof COMPONENTS)[number][0]

const componentId = (code: ComponentCode) =>
  seedId(SEED_GROUP.wmlComponent, scoped(COMPONENTS.findIndex(([c]) => c === code) + 1))

/** [name, from, to, component, is_need_reason, is_reason_required, entity_patch_mapping]. */
const ACTIONS: [
  string,
  RtnStatusCode,
  RtnStatusCode,
  ComponentCode,
  boolean,
  boolean,
  Record<string, unknown> | null
][] = [
  ['ACT_RTN_SUBMIT', 'RTN_DRAFT', 'RTN_PENDING_SPV', 'CMP_RTN_SUBMIT', false, false, null],
  ['ACT_RTN_APP_SPV', 'RTN_PENDING_SPV', 'RTN_PENDING_KADIV', 'CMP_RTN_APP_SPV', false, false, null],
  ['ACT_RTN_REJ_SPV', 'RTN_PENDING_SPV', 'RTN_REJECTED', 'CMP_RTN_REJECT', true, true, null],
  ['ACT_RTN_APP_KADIV', 'RTN_PENDING_KADIV', 'RTN_PENDING_PURCH', 'CMP_RTN_APP_KADIV', false, false, null],
  ['ACT_RTN_REJ_KADIV', 'RTN_PENDING_KADIV', 'RTN_REJECTED', 'CMP_RTN_REJECT', true, true, null],
  ['ACT_RTN_APP_PURCH', 'RTN_PENDING_PURCH', 'RTN_APPROVED', 'CMP_RTN_APP_PURCH', false, false, null],
  ['ACT_RTN_REJ_PURCH', 'RTN_PENDING_PURCH', 'RTN_REJECTED', 'CMP_RTN_REJECT', true, true, null],
  ['ACT_RTN_ASSIGN', 'RTN_APPROVED', 'RTN_IN_PROGRESS', 'CMP_RTN_ASSIGN_OP', false, false, null],
  ['ACT_RTN_TO_STAGING', 'RTN_IN_PROGRESS', 'RTN_READY_STAGING', 'CMP_RTN_SUBMIT_STAGING', false, false, null],
  [
    'ACT_RTN_RECORD_SHIP',
    'RTN_READY_STAGING',
    'RTN_CLOSED',
    'CMP_RTN_SHIP',
    false,
    false,
    { is_shipped: true, shipped_at: '$NOW' }
  ],
  ['ACT_RTN_CANCEL_DRAFT', 'RTN_DRAFT', 'RTN_CANCELLED', 'CMP_RTN_CANCEL', true, true, null],
  ['ACT_RTN_REVISE', 'RTN_REJECTED', 'RTN_PENDING_SPV', 'CMP_RTN_REVISE', false, false, null],
  ['ACT_RTN_CANCEL_STAGING', 'RTN_READY_STAGING', 'RTN_CANCELLED', 'CMP_RTN_CANCEL', true, true, null]
]

export type RtnActionName =
  | 'ACT_RTN_SUBMIT'
  | 'ACT_RTN_APP_SPV'
  | 'ACT_RTN_REJ_SPV'
  | 'ACT_RTN_APP_KADIV'
  | 'ACT_RTN_REJ_KADIV'
  | 'ACT_RTN_APP_PURCH'
  | 'ACT_RTN_REJ_PURCH'
  | 'ACT_RTN_ASSIGN'
  | 'ACT_RTN_TO_STAGING'
  | 'ACT_RTN_RECORD_SHIP'
  | 'ACT_RTN_CANCEL_DRAFT'
  | 'ACT_RTN_REVISE'
  | 'ACT_RTN_CANCEL_STAGING'

export const rtnActionId = (name: RtnActionName) =>
  seedId(SEED_GROUP.wmlAction, scoped(ACTIONS.findIndex(([n]) => n === name) + 1))

export const RTN_ACTION_STATUS = Object.fromEntries(ACTIONS.map(([name, from, to]) => [name, [from, to]])) as Record<
  RtnActionName,
  [RtnStatusCode, RtnStatusCode]
>

const ELIGIBILITY: [RtnActionName, RtnActorCode][] = [
  ['ACT_RTN_SUBMIT', 'ACT_RTN_ADMIN_RM'],
  ['ACT_RTN_SUBMIT', 'ACT_RTN_SPV_OWNER'],
  ['ACT_RTN_SUBMIT', 'ACT_RTN_KADIV_OWNER'],
  ['ACT_RTN_APP_SPV', 'ACT_RTN_SPV_RM'],
  ['ACT_RTN_REJ_SPV', 'ACT_RTN_SPV_RM'],
  ['ACT_RTN_APP_KADIV', 'ACT_RTN_KADIV_RM'],
  ['ACT_RTN_REJ_KADIV', 'ACT_RTN_KADIV_RM'],
  ['ACT_RTN_APP_PURCH', 'ACT_RTN_PURCHASING'],
  ['ACT_RTN_REJ_PURCH', 'ACT_RTN_PURCHASING'],
  ['ACT_RTN_ASSIGN', 'ACT_RTN_ADMIN_RM'],
  ['ACT_RTN_ASSIGN', 'ACT_RTN_SPV_RM'], // [R] BA §RTN-02: Admin atau Supervisor (TL hanya Admin) — keputusan user
  ['ACT_RTN_TO_STAGING', 'ACT_RTN_OP_RM'],
  ['ACT_RTN_RECORD_SHIP', 'ACT_RTN_ADMIN_RM'],
  ['ACT_RTN_CANCEL_DRAFT', 'ACT_RTN_ADMIN_RM'],
  ['ACT_RTN_CANCEL_DRAFT', 'ACT_RTN_SPV_OWNER'],
  ['ACT_RTN_CANCEL_DRAFT', 'ACT_RTN_KADIV_OWNER'],
  ['ACT_RTN_REVISE', 'ACT_RTN_ADMIN_RM'],
  ['ACT_RTN_REVISE', 'ACT_RTN_SPV_OWNER'],
  ['ACT_RTN_REVISE', 'ACT_RTN_KADIV_OWNER'],
  ['ACT_RTN_CANCEL_STAGING', 'ACT_RTN_ADMIN_RM']
]

/** [komponen panel, status, permission, actor]. Baris tanpa actor → [A] semua actor WF-RTN-01 (pola WF-TOB-01). */
const PANELS: [ComponentCode, RtnStatusCode, 'read' | 'write', RtnActorCode | null][] = [
  ['CMP_RTN_PANEL_HEADER', 'RTN_DRAFT', 'write', 'ACT_RTN_ADMIN_RM'],
  ['CMP_RTN_PANEL_HEADER', 'RTN_DRAFT', 'write', 'ACT_RTN_SPV_OWNER'],
  ['CMP_RTN_PANEL_HEADER', 'RTN_DRAFT', 'write', 'ACT_RTN_KADIV_OWNER'],
  ['CMP_RTN_PANEL_HEADER', 'RTN_PENDING_SPV', 'read', null],
  ['CMP_RTN_PANEL_HEADER', 'RTN_CLOSED', 'read', null],
  ['CMP_RTN_PANEL_ITEMS', 'RTN_DRAFT', 'write', 'ACT_RTN_ADMIN_RM'],
  ['CMP_RTN_PANEL_ITEMS', 'RTN_DRAFT', 'write', 'ACT_RTN_SPV_OWNER'],
  ['CMP_RTN_PANEL_ITEMS', 'RTN_DRAFT', 'write', 'ACT_RTN_KADIV_OWNER'],
  ['CMP_RTN_PANEL_ITEMS', 'RTN_PENDING_SPV', 'read', null],
  ['CMP_RTN_PANEL_ITEMS', 'RTN_IN_PROGRESS', 'read', null],
  ['CMP_RTN_PANEL_SHIPPING', 'RTN_READY_STAGING', 'write', 'ACT_RTN_ADMIN_RM'],
  ['CMP_RTN_PANEL_SHIPPING', 'RTN_CLOSED', 'read', null]
]

export function buildWorkflowRtnSeed(): SeedTables {
  const panels: LocalRow[] = PANELS.map(([component, status, permission], index) => ({
    ...seedAudit,
    id: seedId(SEED_GROUP.wmlPanel, scoped(index + 1)),
    component_id: componentId(component),
    status_id: rtnStatusId(status),
    permission
  }))

  return {
    wml_workflow: [
      {
        ...seedAudit,
        id: WF_RTN_01_ID,
        code: 'WF-RTN-01',
        name: 'Return Order RM Workflow',
        entity: 'return_order',
        source_policy: 'manual_create'
      }
    ],
    wml_status: STATUSES.map(([code, name, order, isStart, isTerminal, color, desc], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.wmlStatus, scoped(index + 1)),
      workflow_id: WF_RTN_01_ID,
      code,
      name,
      order,
      is_start: isStart,
      is_terminal: isTerminal,
      is_send_email: false,
      color,
      desc
    })),
    wml_actor: ACTORS.map(([code, name, role, condition]) => ({
      ...seedAudit,
      id: rtnActorId(code),
      workflow_id: WF_RTN_01_ID,
      code,
      name,
      role_id: roleId(role),
      job_fn_id: null,
      condition
    })),
    wml_component: COMPONENTS.map(([code, name, type, uiConfig]) => ({
      ...seedAudit,
      id: componentId(code),
      workflow_id: WF_RTN_01_ID,
      code,
      name,
      type,
      ui_config: uiConfig
    })),
    wml_action: ACTIONS.map(([name, from, to, component, needReason, reasonRequired, patch]) => ({
      ...seedAudit,
      id: rtnActionId(name as RtnActionName),
      workflow_id: WF_RTN_01_ID,
      name,
      from_status_id: rtnStatusId(from),
      to_status_id: rtnStatusId(to),
      component_id: componentId(component),
      is_need_reason: needReason,
      is_reason_required: reasonRequired,
      is_post_closure: false,
      entity_patch_mapping: patch
    })),
    wml_action_actor_eligibility: ELIGIBILITY.map(([action, actor], index) => ({
      ...seedAudit,
      id: seedId(SEED_GROUP.wmlActionActor, scoped(index + 1)),
      action_id: rtnActionId(action),
      actor_id: rtnActorId(actor)
    })),
    wml_panel: panels,
    wml_panel_actor_eligibility: PANELS.flatMap(([, , , actor], index) =>
      (actor ? [actor] : ACTORS.map(([code]) => code)).map((actorCode, offset) => ({
        ...seedAudit,
        id: seedId(SEED_GROUP.wmlPanelActor, scoped((index + 1) * 10 + offset)),
        panel_id: panels[index].id,
        actor_id: rtnActorId(actorCode)
      }))
    )
  }
}
